import { z } from "zod";

import { handleError } from "@/shared/api/error-handler";
import { api, type ResponseConfig } from "@/shared/lib/api";
import { get, post, request } from "@/shared/lib/http/zod-http";
import { getOrganizationsIdMembers } from "@/shared/api/generated-kubb/hooks/useGetOrganizationsIdMembers";

import {
  CreateOrganizationRequestSchema,
  UpdateOrganizationRequestSchema,
  OrganizationSchema,
  OrganizationMemberSchema,
  CreateOrganizationMemberRequestSchema,
  OrganizationStreamerSchema,
  AddStreamerRequestSchema,
} from "./organizations.schemas";
import { parseRequestLoose } from "./parse-request-loose";

export type CreateOrganizationRequest = z.infer<
  typeof CreateOrganizationRequestSchema
>;

export type UpdateOrganizationRequest = z.infer<
  typeof UpdateOrganizationRequestSchema
>;

export type Organization = z.infer<typeof OrganizationSchema>;

export type OrganizationMember = z.infer<typeof OrganizationMemberSchema>;

export type OrganizationStreamer = z.infer<typeof OrganizationStreamerSchema>;

export type AddStreamerRequest = z.infer<typeof AddStreamerRequestSchema>;

const OrganizationsPagedResponseSchema = z.object({
  items: z.array(OrganizationSchema),
  page: z.number(),
  limit: z.number(),
  total: z.number(),
  hasMore: z.boolean(),
});

export type OrganizationsPagedResponse = z.infer<
  typeof OrganizationsPagedResponseSchema
>;

export interface CreateOrganizationMemberRequest {
  email: string;
  roleId: number;
}

export const organizationsApi = {
  async createOrganization(
    data: CreateOrganizationRequest,
  ): Promise<Organization> {
    try {
      const validatedData = parseRequestLoose(CreateOrganizationRequestSchema, data);
      return await post("organizations", validatedData, OrganizationSchema);
    } catch (error) {
      handleError(error, { component: "organizationsApi", action: "create" });
      throw error;
    }
  },

  async getAllOrganizations(): Promise<Organization[]> {
    try {
      return await get(`/organizations/all`, z.array(OrganizationSchema));
    } catch (error) {
      handleError(error, { component: "organizationsApi", action: "fetchAll" });
      throw error;
    }
  },

  async getAllOrganizationsPaged(params?: {
    page?: number;
    limit?: number;
    search?: string;
    filterBy?: "all" | "verified" | "unverified" | string;
    sortBy?: "name" | "members" | "tournaments" | "subscribers" | string;
    sortDir?: "asc" | "desc" | string;
  }): Promise<OrganizationsPagedResponse> {
    try {
      const response = await api.get(`/organizations/all/paged`, { params });
      const parsed = OrganizationsPagedResponseSchema.safeParse(response.data);
      if (!parsed.success) throw parsed.error;
      return parsed.data;
    } catch (error) {
      handleError(error, {
        component: "organizationsApi",
        action: "fetchAllPaged",
      });
      throw error;
    }
  },

  async getUserOrganizations(): Promise<Organization[]> {
    try {
      const result = await get(`/organizations`, z.array(OrganizationSchema));
      return Array.isArray(result) ? result : [];
    } catch (error) {
      handleError(error, {
        component: "organizationsApi",
        action: "fetchByUser",
      });
      return [];
    }
  },

  async getUserOrganizationsById(userId: string): Promise<Organization[]> {
    try {
      const result = await get(
        `/organizations/user/${userId}`,
        z.array(OrganizationSchema),
      );
      return Array.isArray(result) ? result : [];
    } catch (error) {
      // 401 для анонима на публичном профиле — штатный (эндпоинт требует
      // авторизацию). Не логируем как ошибку, просто возвращаем пусто.
      const status = (error as { response?: { status?: number } })?.response?.status;
      if (status !== 401 && status !== 403) {
        handleError(error, {
          component: "organizationsApi",
          action: "fetchByUserId",
        });
      }
      return [];
    }
  },

  async getOrganization(id: string): Promise<Organization> {
    try {
      return await get(`/organizations/${id}`, OrganizationSchema);
    } catch (error) {
      const status = (error as { response?: { status?: number } } | null)
        ?.response?.status;
      // 404 после удаления/переходов — нормальный сценарий, не шумим логами
      if (status !== 404) {
        handleError(error, { component: "organizationsApi", action: "fetchOne" });
      }
      throw error;
    }
  },

  async updateOrganization(
    id: string,
    data: UpdateOrganizationRequest,
  ): Promise<Organization> {
    try {
      const validatedData = parseRequestLoose(UpdateOrganizationRequestSchema, data);
      return await request(
        { url: `/organizations/${id}`, method: "put", data: validatedData },
        OrganizationSchema,
      );
    } catch (error) {
      handleError(error, { component: "organizationsApi", action: "update" });
      throw error;
    }
  },

  async deleteOrganization(id: string, options?: { force?: boolean }): Promise<void> {
    try {
      await request(
        { url: `/organizations/${id}`, method: "delete", params: { force: options?.force } },
        z.unknown(),
      );
    } catch (error) {
      handleError(error, {
        component: "organizationsApi",
        action: "delete",
        meta: { organizationId: id },
      });
      throw error;
    }
  },

  async getOrganizationMembers(
    organizationId: string,
  ): Promise<OrganizationMember[]> {
    try {
      const raw = (await getOrganizationsIdMembers(
        organizationId,
      )) as unknown as ResponseConfig<unknown>;

      const list = Array.isArray(raw.data)
        ? raw.data
        : (raw.data as { members?: unknown[] } | null)?.members;
      if (!Array.isArray(list)) {
        return [];
      }

      const parsed: OrganizationMember[] = [];

      // Ключи с бэка могут прийти в camelCase и PascalCase; значения
      // не доверяем до safeParse.
      type RawOrgMember = Partial<
        Record<
          | "id" | "Id" | "organizationId" | "OrganizationId"
          | "userId" | "UserId" | "userName" | "UserName"
          | "userDisplayName" | "UserDisplayName" | "userEmail" | "UserEmail"
          | "userAvatarUrl" | "UserAvatarUrl" | "twitchLogin" | "TwitchLogin"
          | "roleId" | "RoleId" | "roleKey" | "RoleKey"
          | "roleName" | "RoleName" | "joinedAt" | "JoinedAt"
          | "isOwner" | "IsOwner" | "isBanned" | "IsBanned"
          | "bannedAt" | "BannedAt" | "bannedByUserName" | "BannedByUserName"
          | "isStreamer" | "IsStreamer" | "hasPro" | "HasPro"
          | "proNicknameStyle" | "ProNicknameStyle"
          | "proNicknameColorA" | "ProNicknameColorA"
          | "proNicknameColorB" | "ProNicknameColorB",
          unknown
        >
      >;
      list.forEach((rawMember: unknown) => {
        const m = rawMember as RawOrgMember;
        const candidate = {
          id: m.id ?? m.Id,
          organizationId: m.organizationId ?? m.OrganizationId,
          userId: m.userId ?? m.UserId,
          userName: m.userName ?? m.UserName,
          userDisplayName: m.userDisplayName ?? m.UserDisplayName,
          userEmail: m.userEmail ?? m.UserEmail ?? null,
          userAvatarUrl: m.userAvatarUrl ?? m.UserAvatarUrl ?? null,
          twitchLogin: m.twitchLogin ?? m.TwitchLogin ?? null,
          roleId: m.roleId ?? m.RoleId,
          roleKey: m.roleKey ?? m.RoleKey,
          roleName: m.roleName ?? m.RoleName,
          joinedAt: m.joinedAt ?? m.JoinedAt,
          isOwner: m.isOwner ?? m.IsOwner ?? false,
          isBanned: m.isBanned ?? m.IsBanned ?? false,
          bannedAt: m.bannedAt ?? m.BannedAt ?? null,
          bannedByUserName: m.bannedByUserName ?? m.BannedByUserName,
          isStreamer: m.isStreamer ?? m.IsStreamer ?? false,
          hasPro: m.hasPro ?? m.HasPro ?? false,
          proNicknameStyle: m.proNicknameStyle ?? m.ProNicknameStyle ?? 0,
          proNicknameColorA: m.proNicknameColorA ?? m.ProNicknameColorA ?? null,
          proNicknameColorB: m.proNicknameColorB ?? m.ProNicknameColorB ?? null,
        };

        const res = OrganizationMemberSchema.safeParse(candidate);
        if (res.success) {
          parsed.push(res.data);
        }
      });

      return parsed;
    } catch (error) {
      handleError(error, {
        component: "organizationsApi",
        action: "getMembers",
      });
      throw error;
    }
  },

  async getOrganizationTeam(
    organizationId: string,
  ): Promise<OrganizationMember[]> {
    try {
      const res = await api.get(`/organizations/${organizationId}/team`);
      const list = Array.isArray(res.data) ? res.data : [];
      const parsed: OrganizationMember[] = [];
      const pick = (row: unknown, a: string, b: string): unknown => {
        const o = row as Record<string, unknown>;
        return o[a] ?? o[b];
      };
      list.forEach((m: unknown) => {
        const candidate = {
          id: pick(m, "id", "Id"),
          organizationId: pick(m, "organizationId", "OrganizationId"),
          userId: pick(m, "userId", "UserId"),
          userName: pick(m, "userName", "UserName"),
          userDisplayName: pick(m, "userDisplayName", "UserDisplayName"),
          userEmail: pick(m, "userEmail", "UserEmail") ?? null,
          userAvatarUrl: pick(m, "userAvatarUrl", "UserAvatarUrl") ?? null,
          twitchLogin: pick(m, "twitchLogin", "TwitchLogin") ?? null,
          roleId: pick(m, "roleId", "RoleId"),
          roleKey: pick(m, "roleKey", "RoleKey"),
          roleName: pick(m, "roleName", "RoleName"),
          joinedAt: pick(m, "joinedAt", "JoinedAt"),
          isOwner: pick(m, "isOwner", "IsOwner") ?? false,
          isBanned: pick(m, "isBanned", "IsBanned") ?? false,
          bannedAt: pick(m, "bannedAt", "BannedAt") ?? null,
          bannedByUserName: pick(m, "bannedByUserName", "BannedByUserName"),
          isStreamer: pick(m, "isStreamer", "IsStreamer") ?? false,
          hasPro: pick(m, "hasPro", "HasPro") ?? false,
          proNicknameStyle: pick(m, "proNicknameStyle", "ProNicknameStyle") ?? 0,
          proNicknameColorA:
            pick(m, "proNicknameColorA", "ProNicknameColorA") ?? null,
          proNicknameColorB:
            pick(m, "proNicknameColorB", "ProNicknameColorB") ?? null,
        };
        const r = OrganizationMemberSchema.safeParse(candidate);
        if (r.success) parsed.push(r.data);
      });
      return parsed;
    } catch (error) {
      handleError(error, { component: "organizationsApi", action: "getTeam" });
      throw error;
    }
  },

  async getOrganizationBlacklist(organizationId: string): Promise<
    Array<{
      userId: string;
      userDisplayName: string;
      reason: string;
      createdAt: string;
    }>
  > {
    const res = await api.get(`/organizations/${organizationId}/blacklist`);
    const raw = (res.data as { blacklist?: unknown[] })?.blacklist ?? [];
    return raw.map((x: unknown) => {
      const o = x as Record<string, unknown>;
      return {
        userId: String(o["userId"] ?? o["UserId"] ?? ""),
        userDisplayName: String(
          o["userDisplayName"] ?? o["UserDisplayName"] ?? o["userId"] ?? "",
        ),
        reason: String(o["reason"] ?? o["Reason"] ?? ""),
        createdAt: String(o["createdAt"] ?? o["CreatedAt"] ?? ""),
      };
    });
  },

  async addOrganizationBlacklist(
    organizationId: string,
    body: { username?: string; userId?: string; reason?: string },
  ): Promise<void> {
    await api.post(`/organizations/${organizationId}/blacklist`, body);
  },

  async removeOrganizationBlacklistEntry(
    organizationId: string,
    userId: string,
  ): Promise<void> {
    await api.delete(`/organizations/${organizationId}/blacklist/${userId}`);
  },

  async addOrganizationMember(
    organizationId: string,
    data: CreateOrganizationMemberRequest,
  ): Promise<OrganizationMember> {
    try {
      const validatedData = parseRequestLoose(CreateOrganizationMemberRequestSchema, data);
      return post(
        `/organizations/${organizationId}/members`,
        validatedData,
        OrganizationMemberSchema,
      );
    } catch (error) {
      handleError(error, {
        component: "organizationsApi",
        action: "addMember",
      });
      throw error;
    }
  },

  async removeOrganizationMember(
    organizationId: string,
    userId: string,
  ): Promise<void> {
    try {
      await request(
        {
          url: `/organizations/${organizationId}/members/${userId}`,
          method: "delete",
        },
        z.void(),
      );
    } catch (error) {
      handleError(error, {
        component: "organizationsApi",
        action: "removeMember",
      });
      throw error;
    }
  },

  async banOrganizationMember(
    organizationId: string,
    userId: string,
    reason: string,
  ): Promise<void> {
    try {
      await post(
        `/organizations/${organizationId}/members/${userId}/ban`,
        { reason },
        z.void(),
      );
    } catch (error) {
      handleError(error, {
        component: "organizationsApi",
        action: "banMember",
      });
      throw error;
    }
  },

  async unbanOrganizationMember(
    organizationId: string,
    userId: string,
  ): Promise<void> {
    try {
      await post(
        `/organizations/${organizationId}/members/${userId}/unban`,
        {},
        z.void(),
      );
    } catch (error) {
      handleError(error, {
        component: "organizationsApi",
        action: "unbanMember",
      });
      throw error;
    }
  },

  async updateMemberRole(
    organizationId: string,
    userId: string,
    roleIdOrKey: number | string,
  ): Promise<OrganizationMember> {
    try {
      const data =
        typeof roleIdOrKey === "string"
          ? { roleKey: roleIdOrKey }
          : { roleId: roleIdOrKey };

      return request(
        {
          url: `/organizations/${organizationId}/members/${userId}/role`,
          method: "put",
          data,
        },
        OrganizationMemberSchema,
      );
    } catch (error) {
      handleError(error, {
        component: "organizationsApi",
        action: "updateMemberRole",
      });
      throw error;
    }
  },

  async updateMemberStreamerStatus(
    organizationId: string,
    userId: string,
    isStreamer: boolean,
  ): Promise<OrganizationMember> {
    try {
      return request(
        {
          url: `/organizations/${organizationId}/members/${userId}/streamer`,
          method: "put",
          data: { isStreamer },
        },
        OrganizationMemberSchema,
      );
    } catch (error) {
      handleError(error, {
        component: "organizationsApi",
        action: "updateMemberStreamerStatus",
      });
      throw error;
    }
  },

  // Subscriptions
  async subscribe(organizationId: string): Promise<{ success: boolean }> {
    return post(
      `/organizations/${organizationId}/subscribe`,
      {},
      z.object({ success: z.boolean() }),
    );
  },

  async unsubscribe(organizationId: string): Promise<{ success: boolean }> {
    return post(
      `/organizations/${organizationId}/unsubscribe`,
      {},
      z.object({ success: z.boolean() }),
    );
  },

  async getMySubscriptions(): Promise<
    Array<{ organizationId: string; createdAt: string }>
  > {
    return get(
      `/organizations/my-subscriptions`,
      z.array(
        z.object({
          organizationId: z.string(),
          createdAt: z.string(),
        }),
      ),
    );
  },

  async leaveOrganization(
    organizationId: string,
  ): Promise<{ success: boolean; message: string }> {
    try {
      return post(
        `/organizations/${organizationId}/leave`,
        {},
        z.object({ success: z.boolean(), message: z.string() }),
      );
    } catch (error) {
      handleError(error, {
        component: "organizationsApi",
        action: "leaveOrganization",
      });
      throw error;
    }
  },

  async transferOwnership(
    organizationId: string,
    newOwnerId: string,
  ): Promise<{ success: boolean; message: string }> {
    try {
      return post(
        `/organizations/${organizationId}/transfer-ownership`,
        { newOwnerId },
        z.object({ success: z.boolean(), message: z.string() }),
      );
    } catch (error) {
      handleError(error, {
        component: "organizationsApi",
        action: "transferOwnership",
        organizationId,
        newOwnerId,
      });
      throw error;
    }
  },

  async addStreamer(
    organizationId: string,
    data: AddStreamerRequest,
  ): Promise<OrganizationStreamer> {
    try {
      const validatedData = parseRequestLoose(AddStreamerRequestSchema, data);
      return await post(
        `/organizations/${organizationId}/streamers`,
        validatedData,
        OrganizationStreamerSchema,
      );
    } catch (error) {
      handleError(error, {
        component: "organizationsApi",
        action: "addStreamer",
        organizationId,
      });
      throw error;
    }
  },

  async removeStreamer(
    organizationId: string,
    streamerId: string,
  ): Promise<void> {
    try {
      await request(
        {
          url: `/organizations/${organizationId}/streamers/${streamerId}`,
          method: "delete",
        },
        z.void(),
      );
    } catch (error) {
      handleError(error, {
        component: "organizationsApi",
        action: "removeStreamer",
        organizationId,
        streamerId,
      });
      throw error;
    }
  },

  async updateStreamerSettings(
    organizationId: string,
    streamerId: string,
    data: { isVisible?: boolean; displayOrder?: number },
  ): Promise<OrganizationStreamer> {
    try {
      return await request(
        {
          url: `/organizations/${organizationId}/streamers/${streamerId}/settings`,
          method: "put",
          data,
        },
        OrganizationStreamerSchema,
      );
    } catch (error) {
      handleError(error, {
        component: "organizationsApi",
        action: "updateStreamerSettings",
        organizationId,
        streamerId,
      });
      throw error;
    }
  },

  async getOrganizationTwitchOAuthUrl(
    organizationId: string,
    redirectUri: string,
  ): Promise<string> {
    try {
      const res = await post(
        `/organizations/${organizationId}/oauth/twitch/url`,
        { redirectUri },
        z.object({ url: z.string().min(1) }),
      );
      return res.url;
    } catch (error) {
      handleError(error, {
        component: "organizationsApi",
        action: "getOrganizationTwitchOAuthUrl",
        organizationId,
      });
      throw error;
    }
  },

  async connectOrganizationTwitchOAuth(
    organizationId: string,
    data: { code: string; state: string; redirectUri: string },
  ): Promise<{ success: boolean; login?: string }> {
    try {
      return await post(
        `/organizations/${organizationId}/oauth/twitch/callback`,
        data,
        z.object({
          success: z.boolean(),
          login: z.string().optional(),
        }),
      );
    } catch (error) {
      handleError(error, {
        component: "organizationsApi",
        action: "connectOrganizationTwitchOAuth",
        organizationId,
      });
      throw error;
    }
  },

  async disconnectOrganizationTwitchOAuth(
    organizationId: string,
  ): Promise<{ success: boolean }> {
    try {
      return await post(
        `/organizations/${organizationId}/oauth/twitch/disconnect`,
        {},
        z.object({ success: z.boolean() }),
      );
    } catch (error) {
      handleError(error, {
        component: "organizationsApi",
        action: "disconnectOrganizationTwitchOAuth",
        organizationId,
      });
      throw error;
    }
  },

  members: async (organizationId: string): Promise<OrganizationMember[]> => {
    return organizationsApi.getOrganizationMembers(organizationId);
  },
};
